'use client'

import { useState, useEffect, useRef } from 'react'
import { useScrollLock } from '@/lib/hooks'
import { X, ArrowLeft } from 'lucide-react'
import { authService } from '@/lib/services/auth'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { analytics } from '@/lib/analytics'
import { hardNavigate } from '@/lib/auth/hardNavigate'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/Input'
import { OTPInput } from '@/components/ui/OTPInput'
import EmailTypoPrompt from '@/components/auth/EmailTypoPrompt'
import GoogleNudge from '@/components/auth/GoogleNudge'
import OpenGmailButton from '@/components/auth/OpenGmailButton'
import EmailTapAnimation from '@/components/auth/EmailTapAnimation'
import { useGoogleNudge } from '@/lib/auth/useGoogleNudge'
import { suggestEmailFix } from '@/lib/auth/emailTypo'
import { RESEND_COOLDOWN_SECONDS, rateLimitSeconds, useResendCooldown } from '@/lib/auth/useResendCooldown'
import { clearPendingSignIn, savePendingSignIn, type PendingSignIn } from '@/lib/auth/pendingSignIn'

interface SimpleAuthModalProps {
  isOpen: boolean
  onClose: () => void
  defaultMode?: 'signin' | 'signup'
  /** Where sign-in lands - by link, code or Google. Default: the current page. */
  returnTo?: string
  /** A sign-in that was waiting for its code when the tab reloaded: opens on the code screen. */
  resume?: PendingSignIn
}

export default function SimpleAuthModal({ isOpen, onClose, returnTo, resume }: SimpleAuthModalProps) {
  const { t, language } = useLanguage()
  const [view, setView] = useState<'main' | 'collect-names' | 'otp'>(resume ? 'otp' : 'main')
  const [formData, setFormData] = useState({
    email: resume?.email ?? '',
    firstName: resume?.firstName ?? '',
    lastName: resume?.lastName ?? '',
    otp: ''
  })
  const [isNewUser, setIsNewUser] = useState(resume?.isNewUser ?? false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [typoSuggestion, setTypoSuggestion] = useState<string | null>(null)
  // The address the person confirmed as typed, despite a suggestion.
  const [typoKeptFor, setTypoKeptFor] = useState<string | null>(null)
  const [googleMail, setGoogleMail] = useState(resume?.googleMail ?? false)
  const [failedCodes, setFailedCodes] = useState(0)
  const [resends, setResends] = useState(0)
  const [resent, setResent] = useState(false)
  const cooldown = useResendCooldown()
  const modalRef = useRef<HTMLDivElement>(null)

  // `loading` is React state: two submits in the same tick both observe
  // loading === false and both fire, which is the 35ms double-submit seen on
  // /auth/v1/otp and /auth/v1/verify in the logs. A ref latches synchronously
  // and closes that window. One ref per handler - a user legitimately
  // verifies a code after requesting one, so a shared ref would wrongly
  // block that.
  const emailContinueSubmitting = useRef(false)
  const signUpSubmitting = useRef(false)
  const verifyOtpSubmitting = useRef(false)
  const resendCodeSubmitting = useRef(false)
  const googleSignInSubmitting = useRef(false)

  // Centralized scroll lock
  useScrollLock(isOpen)

  // A resumed code screen keeps the send-again countdown of the code it is waiting for.
  const startCooldown = cooldown.start
  useEffect(() => {
    if (!resume) return
    const left = RESEND_COOLDOWN_SECONDS - Math.floor((Date.now() - resume.sentAt) / 1000)
    if (left > 0) startCooldown(left)
  }, [resume, startCooldown])

  // Closing on purpose drops the pending sign-in: a later reload must not reopen it.
  const close = () => {
    clearPendingSignIn()
    onClose()
  }

  // Escape key and focus management
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearPendingSignIn()
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)

      // Focus first interactive element
      const firstFocusable = modalRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement
      firstFocusable?.focus()
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // A Google mailbox is offered Google sign-in after 30 s, or soon after a wrong code or resend.
  const showGoogleNudge = useGoogleNudge(googleMail, failedCodes + resends)

  if (!isOpen) return null

  // Step 1: User enters email, we check if they exist
  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email) {
      setError(t('authModal.pleaseEnterEmail'))
      return
    }

    const suggestion = suggestEmailFix(formData.email)
    if (suggestion && typoKeptFor !== formData.email) {
      setTypoSuggestion(suggestion)
      return
    }
    await continueWithEmail(formData.email)
  }

  const continueWithEmail = async (email: string) => {
    setTypoSuggestion(null)
    if (emailContinueSubmitting.current) return
    emailContinueSubmitting.current = true

    setLoading(true)
    setError('')

    try {
      // Check if profile exists
      const { data: profile } = await authService.checkProfileExists(email)

      if (profile) {
        // User exists → Send magic link immediately (no metadata needed)
        setIsNewUser(false)
        const currentPath = returnTo ?? window.location.pathname + window.location.search
        const sent = await authService.sendMagicLink(email, undefined, currentPath, language)
        onCodeSent(sent, email, false)
        analytics.auth.magicLinkSent({ is_new_user: false })
        setView('otp')
      } else {
        // User doesn't exist → Show name collection form
        setIsNewUser(true)
        setView('collect-names')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ''
      // Parse rate limit error
      const seconds = rateLimitSeconds(errorMessage)
      if (seconds !== null) {
        cooldown.start(seconds)
        setError(t('authModal.rateLimitError').replace('{seconds}', String(seconds)))
      } else {
        setError(errorMessage || t('authModal.failedToSendMagicLink'))
      }
    } finally {
      setLoading(false)
      emailContinueSubmitting.current = false
    }
  }

  // A different address starts clean (no inherited nudge or resend banner).
  const resetTroubleState = () => {
    setFailedCodes(0)
    setResends(0)
    setResent(false)
    setGoogleMail(false)
  }

  const onCodeSent = (sent: { googleMail?: boolean } | undefined, email: string, newUser: boolean) => {
    setGoogleMail(sent?.googleMail === true)
    cooldown.start()
    savePendingSignIn({
      email,
      firstName: newUser ? formData.firstName.trim() : '',
      lastName: newUser ? formData.lastName.trim() : '',
      isNewUser: newUser,
      googleMail: sent?.googleMail === true,
      returnTo,
      path: window.location.pathname + window.location.search,
      sentAt: Date.now(),
    })
  }

  // Step 2: New user provides names, then we send magic link with metadata
  const handleSignUpWithNames = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError(t('authModal.pleaseEnterName'))
      return
    }

    if (signUpSubmitting.current) return
    signUpSubmitting.current = true

    setLoading(true)
    setError('')

    try {
      // Send magic link with user metadata for signup
      const currentPath = returnTo ?? window.location.pathname + window.location.search
      const sent = await authService.sendMagicLink(formData.email, {
        data: {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          auth_provider: 'email'
        }
      }, currentPath, language)
      onCodeSent(sent, formData.email, true)
      analytics.auth.magicLinkSent({ is_new_user: true })
      setView('otp')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ''
      // Parse rate limit error
      const seconds = rateLimitSeconds(errorMessage)
      if (seconds !== null) {
        cooldown.start(seconds)
        setError(t('authModal.rateLimitError').replace('{seconds}', String(seconds)))
      } else {
        setError(errorMessage || t('authModal.failedToSendMagicLink'))
      }
    } finally {
      setLoading(false)
      signUpSubmitting.current = false
    }
  }

  const handleVerifyOtp = async (code: string) => {
    if (!code || code.length !== 6) {
      setError(t('authModal.invalidCode'))
      return
    }

    if (verifyOtpSubmitting.current) return
    verifyOtpSubmitting.current = true

    setLoading(true)
    setError('')

    try {
      await authService.verifyOtp(formData.email, code, isNewUser)
      analytics.auth.otpSuccess({ is_new_user: isNewUser })
      clearPendingSignIn()
      // Finish with a FULL navigation, like the magic link does through
      // /api/auth/callback: the middleware then writes the auth header and the
      // layout renders user + profile server-side. A soft push/refresh left
      // the server snapshot logged-out and made the header depend on a
      // client-side profile fetch – which a jammed browser cannot make
      // (2026-09-17). Land where the link would: `returnTo` when given, else
      // the dashboard from the home page (matches the callback), else reload
      // the page the person is on.
      hardNavigate(returnTo ?? (window.location.pathname === '/' ? '/dashboard' : window.location.href))
      onClose()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message.toLowerCase() : ''
      analytics.auth.otpFailure({ is_new_user: isNewUser, error_message: errorMessage })
      setFailedCodes((n) => n + 1)
      if (errorMessage.includes('expired')) {
        setError(t('authModal.codeRejected'))
      } else {
        setError(t('authModal.invalidCode'))
      }
    } finally {
      setLoading(false)
      verifyOtpSubmitting.current = false
    }
  }

  const handleResendCode = async () => {
    if (resendCodeSubmitting.current) return
    resendCodeSubmitting.current = true

    setLoading(true)
    setError('')
    setResent(false)

    try {
      // Pass metadata only if it was a sign up (names are filled)
      const options = (formData.firstName.trim() && formData.lastName.trim()) ? {
        data: {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          auth_provider: 'email'
        }
      } : undefined

      const currentPath = returnTo ?? window.location.pathname + window.location.search
      const sent = await authService.sendMagicLink(formData.email, options, currentPath, language)
      onCodeSent(sent, formData.email, !!options)
      setResends((n) => n + 1)
      setResent(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ''
      // Parse rate limit error
      const seconds = rateLimitSeconds(errorMessage)
      if (seconds !== null) {
        cooldown.start(seconds)
        setError(t('authModal.rateLimitError').replace('{seconds}', String(seconds)))
      } else {
        setError(errorMessage || t('authModal.failedToSendMagicLink'))
      }
    } finally {
      setLoading(false)
      resendCodeSubmitting.current = false
    }
  }

  const startGoogleSignIn = async () => {
    if (googleSignInSubmitting.current) return
    googleSignInSubmitting.current = true
    try {
      setLoading(true)
      analytics.auth.googleSignInStarted()
      await authService.signInWithGoogle(returnTo ?? window.location.pathname + window.location.search)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('authModal.failedToSignInWithGoogle'))
      setLoading(false)
      googleSignInSubmitting.current = false
    }
  }


  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={close}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg max-w-md w-full relative max-h-[calc(100dvh-2rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
          {view === 'main' ? (
            // Main View
            <>
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-200">
                <Button
                  variant="ghost"
                  onClick={close}
                  className="absolute top-5 right-5 p-1 pointer-coarse:p-2 h-auto text-gray-400 hover:text-gray-600"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </Button>
                <h2 id="modal-title" className="text-2xl font-bold text-gray-900 pr-8">
                  {t('authModal.welcomeBack')}
                </h2>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Email Sign-in */}
                <form onSubmit={handleEmailContinue} className="space-y-4">
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => { setTypoSuggestion(null); setFormData(prev => ({ ...prev, email: e.target.value })) }}
                    label={t('authModal.emailAddress')}
                    placeholder={t('authModal.emailPlaceholder')}
                    required
                    autoComplete="email"
                    autoFocus
                  />

                  {typoSuggestion ? (
                    <EmailTypoPrompt
                      typed={formData.email}
                      suggestion={typoSuggestion}
                      onUseSuggestion={() => {
                        setFormData(prev => ({ ...prev, email: typoSuggestion }))
                        void continueWithEmail(typoSuggestion)
                      }}
                      onKeepTyped={() => {
                        setTypoKeptFor(formData.email)
                        void continueWithEmail(formData.email)
                      }}
                    />
                  ) : (
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full"
                    >
                      {loading ? t('authModal.checking') : t('authModal.continue')}
                    </Button>
                  )}
                </form>

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('authModal.or')}</span>
                  </div>
                </div>

                {/* Google Sign-in */}
                <Button
                  variant="outline"
                  onClick={startGoogleSignIn}
                  disabled={loading}
                  className="w-full px-4 py-3 h-auto text-gray-700 font-semibold flex items-center justify-center gap-3"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t('authModal.continueWithGoogle')}
                </Button>
              </div>
            </>
          ) : view === 'collect-names' ? (
            // Collect Names View (for new users)
            <>
              {/* Header with back button */}
              <div className="px-6 py-5 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setView('main')}
                    className="p-1 pointer-coarse:p-2 h-auto text-gray-400 hover:text-gray-600"
                    aria-label="Back to email view"
                  >
                    <ArrowLeft size={24} />
                  </Button>
                  <div>
                    <h3 id="modal-title" className="text-xl font-semibold text-gray-900">
                      {t('authModal.createYourAccount')}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSignUpWithNames} className="space-y-4">
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    label={t('authModal.firstName')}
                    placeholder={t('authModal.firstNamePlaceholder')}
                    required
                    autoComplete="given-name"
                    autoFocus
                  />

                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    label={t('authModal.lastName')}
                    placeholder={t('authModal.lastNamePlaceholder')}
                    required
                    autoComplete="family-name"
                  />

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? t('authModal.sending') : t('authModal.createAccount')}
                  </Button>
                </form>

                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    {t('authModal.sendSecureLinkSetup')}
                  </p>
                </div>
              </div>
            </>
          ) : (
            // Check Email / OTP Verification View
            <>
              {/* Header with back button */}
              <div className="px-6 py-5 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => { clearPendingSignIn(); setView('main'); resetTroubleState(); setFormData(prev => ({ ...prev, otp: '' })) }}
                    className="p-1 pointer-coarse:p-2 h-auto text-gray-400 hover:text-gray-600"
                    aria-label="Back"
                  >
                    <ArrowLeft size={24} />
                  </Button>
                  <div>
                    <h3 id="modal-title" className="text-xl font-semibold text-gray-900">
                      {t('authModal.checkYourEmail')}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Primary: Check email for magic link */}
                <div className="text-center space-y-4">
                  <EmailTapAnimation label={t('authModal.clickButtonInEmail')} />
                  <div>
                    <p className="text-base text-gray-700">
                      {t('authModal.magicLinkInstructions')}
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {formData.email}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500">
                    {t('authModal.clickButtonInEmail')}
                  </p>
                </div>

                {googleMail && <OpenGmailButton email={formData.email} />}

                {showGoogleNudge && <GoogleNudge email={formData.email} onGoogle={startGoogleSignIn} disabled={loading} />}

                {resent && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">{t('authModal.newCodeSent')}</p>
                  </div>
                )}

                {/* Resend link */}
                <div className="text-center space-y-1">
                  <Button
                    variant="link"
                    onClick={handleResendCode}
                    disabled={loading || cooldown.secondsLeft > 0}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium p-0 pointer-coarse:px-3 h-auto pointer-coarse:min-h-10"
                  >
                    {cooldown.secondsLeft > 0
                      ? t('authModal.resendIn').replace('{seconds}', String(cooldown.secondsLeft))
                      : t('authModal.resendEmail')}
                  </Button>
                </div>

                {/* Secondary: Manual OTP entry */}
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <p className="text-xs text-gray-400 text-center">
                    {t('authModal.enterCodeManually')}
                  </p>
                  <OTPInput
                    value={formData.otp}
                    onChange={(value) => setFormData(prev => ({ ...prev, otp: value }))}
                    onComplete={handleVerifyOtp}
                    length={6}
                    disabled={loading}
                  />
                </div>
              </div>
            </>
          )}
      </div>
    </div>
  )
}
